<template>
    <div class="defaultOptionsInnerDiv">
        <div class="intervalTypeOptionDiv">
            <cic-select
                :options="[
                    { value: 0, label: $t('Duration') },
                    { value: 1, label: $t('Sessions') },
                    { value: 2, label: $t('Date Time') },
                ]"
                :value="item.intervalType === 5 ? 0 : item.intervalType === 3 ? 2 : item.intervalType"
                @input="updateInterval"
            />
        </div>
        <div v-if="[0, 5].includes(item.intervalType)" class="intervalOptionDiv">
            <cic-select :options="times" :value="item.intervalMS" @input="updateDuration" />
        </div>
        <div
            v-if="[0, 5].includes(item.intervalType) && item.intervalMS >= 24 * 60"
            class="retryIntervalSessionRotationDiv"
        >
            <span>{{ $t('Session Rotation') }}</span>
            <cic-checkbox
                :value="item.intervalType === 5"
                @input="(val) => updateFieldValue('intervalType', val ? 5 : 0)"
            />
            <div class="question-mark-icon" :title="sessionRotationTooltip">?</div>
        </div>
        <div v-if="item.intervalType === 1" class="sessionOptionDiv">
            <cic-textbox
                :value="item.intervalSessions"
                :int-only="true"
                :maxlength="2"
                @input="(val) => updateFieldValue('intervalSessions', val)"
            />
            <div style="cursor: help" :title="sessionTooltip">{{ $t('Sessions') }}</div>
            <div class="question-mark-icon" :title="sessionTooltip">?</div>
        </div>
        <div v-if="[2, 3].includes(item.intervalType)" class="datetimeOptionDiv">
            <div class="dateOptionInnerDiv">
                <cic-date-picker
                    :value="toMoment(item.retryDate)"
                    :clearable="false"
                    @input="(val) => updateFieldValue('retryDate', val.format('YYYY-MM-DD'))"
                />
            </div>
            <div class="timeOptionInnerDiv">
                <cic-time-picker
                    :value="item.retryTime"
                    :clearable="false"
                    format="HH:mm"
                    @input="(val) => updateFieldValue('retryTime', val)"
                />
            </div>
            <span>{{ $t('Retry past dates') }}</span>
            <cic-checkbox
                :value="item.intervalType === 2"
                @input="(val) => updateFieldValue('intervalType', val ? 2 : 3)"
            />
            <div class="question-mark-icon" :title="$t('RETRY_PAST_DATES_DESCRIPTION')">?</div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import CicDatePicker from '@/components/CicDatePicker';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import CicTimePicker from '@/components/inputs/CicTimePicker';
    import { showValidationMessage } from '@/utils/Notification';

    export default {
        name: 'EditInterval',
        components: {
            CicCheckbox,
            CicDatePicker,
            CicSelect,
            CicTextbox,
            CicTimePicker,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
            parameters: {
                type: Object,
                required: true,
            },
        },
        emits: ['input'],
        computed: {
            sessions() {
                let sessions = [];
                let sessionID = 1;
                while (
                    sessionID < 10 &&
                    this.parameters['DialRetrySessionStartTime' + sessionID.toString()] &&
                    this.parameters['DialRetrySessionEndTime' + sessionID.toString()]
                ) {
                    sessions.push(
                        this.parameters['DialRetrySessionStartTime' + sessionID.toString()] +
                            '-' +
                            this.parameters['DialRetrySessionEndTime' + sessionID.toString()],
                    );

                    sessionID++;
                }

                return sessions;
            },
            sessionTooltip() {
                return this.$t('SESSION_DESCRIPTION') + this.sessions.join(', ');
            },
            sessionRotationTooltip() {
                return this.$t('SESSION_ROTATION_DESCRIPTION') + this.sessions.join(', ');
            },
            times() {
                return [{ value: 1, label: this.$t('1 minute') }]
                    .concat(
                        [2, 3, 4, 5, 10, 15, 20, 25, 30, 45, 60, 90].map((x) => ({
                            value: x,
                            label: this.$t('[x] minutes').replace(/\[x]/g, x),
                        })),
                    )
                    .concat(
                        [2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 18].map((x) => ({
                            value: x * 60,
                            label: this.$t('[x] hours').replace(/\[x]/g, x),
                        })),
                    )
                    .concat([
                        {
                            value: 24 * 60,
                            label: this.$t('1 day'),
                        },
                    ])
                    .concat(
                        [2, 3, 4, 5, 6, 7].map((x) => ({
                            value: x * 24 * 60,
                            label: this.$t('[x] days').replace(/\[x]/g, x),
                        })),
                    );
            },
        },
        methods: {
            toMoment(date) {
                return moment(date);
            },

            updateDuration(val) {
                const minDuration = Math.floor(
                    (this.parameters.REDDIAL_RETRY_PROFILE_ABANDONED_MIN_INTERVAL ?? 0) / 60000,
                );
                if (this.item.code?.toLowerCase() === 'abandoned' && minDuration > 0 && val < minDuration) {
                    showValidationMessage([
                        '[x]',
                        minDuration,
                        'Duration cannot be less than the retry profile abandoned minimum interval of [x] minutes',
                    ]);
                    return;
                }

                const updates = [{ field: 'intervalMS', value: val }];
                if (val < 24 * 60 && this.item.intervalType === 5) {
                    updates.push({ field: 'intervalType', value: 0 });
                }

                this.$emit('input', updates);
            },

            updateFieldValue(field, val) {
                this.$emit('input', [{ field: field, value: val }]);
            },

            updateInterval(val) {
                let updates = [{ field: 'intervalType', value: val }];
                if (val === 2) {
                    updates.push({ field: 'retryDate', value: moment().format('YYYY-MM-DD') });
                    updates.push({ field: 'retryTime', value: moment().format('HH:mm') });
                }

                this.$emit('input', updates);
            },
        },
    };
</script>
